var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState, useContext, createContext } from 'react';
import { startOfToday } from 'date-fns';
import { useJsApiLoader } from '@react-google-maps/api';
import { Card } from '@aptive-env/storybook';
import MiniCalendar from 'components/shared/MiniCalendar';
import CalendarDayList from 'components/CalendarDayList';
import { fetchCalendarEventTypes, fetchCurrentWeekCalendarEventsWithWeatherForecasts, fetchOfficeEmployees, } from 'services/calendar';
import { AptiveIcon } from '@aptive-env/storybook';
import { fetchRegions } from 'services/region';
import { OfficeModal } from 'components/OfficeModal';
import { EventUpdateModal } from 'components/EventUpdateModal';
import OfficeCalendar from 'components/OfficeCalendar';
import { CalendarStateContext } from 'context/CalendarContext';
import { eventConstants } from 'constants/index';
import NoOffice from 'components/NoOffice';
import { useToast } from 'hooks/useToast';
import { getErrorMessage } from 'utils/error';
var EVENT_NEW_TYPE = eventConstants.EVENT_NEW_TYPE;
export var AppContext = createContext({
    isGoogleMapAPILoaded: false,
    googleMapAPILoadError: undefined,
});
var libraries = ['drawing', 'places', 'marker'];
var Calendar = function () {
    var _a, _b;
    var _c = useContext(CalendarStateContext), eventTypes = _c.eventTypes, setEventTypes = _c.setEventTypes, selectedOffice = _c.selectedOffice, setSelectedOffice = _c.setSelectedOffice, setOfficeEmployees = _c.setOfficeEmployees, calendarMode = _c.calendarMode, setCalendarMode = _c.setCalendarMode;
    var setToast = useToast().setToast;
    var _d = useState([]), regions = _d[0], setRegions = _d[1];
    var _e = useState(startOfToday()), currentDate = _e[0], setCurrentDate = _e[1];
    var _f = useState([]), currentWeekDates = _f[0], setCurrentWeekDates = _f[1];
    var _g = useState(false), isNewEventModalOpen = _g[0], setIsNewEventModalOpen = _g[1];
    var _h = useState(false), isOfficeModalOpen = _h[0], setIsOfficeModalOpen = _h[1];
    var _j = useState(0), officeCalendarKey = _j[0], setOfficeCalendarKey = _j[1];
    var _k = useState(false), noOfficeModalOpen = _k[0], setNoOfficeModalOpen = _k[1];
    var _l = useJsApiLoader({
        googleMapsApiKey: (_a = process.env.REACT_APP_GOOGLE_MAPS_API_KEY) !== null && _a !== void 0 ? _a : '',
        mapIds: [(_b = process.env.REACT_APP_GOOGLE_MAPS_ID) !== null && _b !== void 0 ? _b : ''],
        libraries: libraries,
    }), isLoaded = _l.isLoaded, loadError = _l.loadError;
    var handleDateChange = useCallback(function (date) { return setCurrentDate(date); }, []);
    var openOfficeSelectionModal = function () {
        setIsOfficeModalOpen(true);
    };
    var closeOfficeSelectionModal = function () {
        setIsOfficeModalOpen(false);
    };
    var handleChangeOffice = function (office) {
        setSelectedOffice(office);
        closeOfficeSelectionModal();
    };
    var openNewEventModal = function () {
        setIsNewEventModalOpen(true);
    };
    var closeNewEventModal = function () {
        setIsNewEventModalOpen(false);
    };
    var handleEventSuccess = function (success) {
        setToast({
            status: 'success',
            message: success,
        });
        closeNewEventModal();
        if (!selectedOffice)
            return;
        fetchCurrentWeekCalendarEventsWithWeatherForecasts(selectedOffice)
            .then(function (_a) {
            var data = _a.data, error = _a.error;
            setCurrentWeekDates(data);
            if (error) {
                setToast({
                    status: 'danger',
                    message: error,
                });
            }
        })
            .catch(function (e) {
            setToast({
                status: 'danger',
                message: getErrorMessage(e),
            });
        });
        setOfficeCalendarKey(officeCalendarKey + 1);
    };
    useEffect(function () {
        if (!selectedOffice)
            return;
        fetchCurrentWeekCalendarEventsWithWeatherForecasts(selectedOffice)
            .then(function (_a) {
            var data = _a.data, error = _a.error;
            setCurrentWeekDates(data);
        })
            .catch(function (e) {
            setToast({
                status: 'danger',
                message: getErrorMessage(e)
            });
        });
        fetchOfficeEmployees(selectedOffice.id)
            .then(function (items) {
            setOfficeEmployees(items);
        })
            .catch(function (error) {
            setToast({
                status: 'danger',
                message: getErrorMessage(error)
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOffice]);
    useEffect(function () {
        fetchRegions()
            .then(function (regions) {
            setRegions(regions);
            if (regions.length > 0) {
                setSelectedOffice(regions[0].offices[0]);
            }
            else {
                setNoOfficeModalOpen(true);
            }
        })
            .catch(function (error) {
            setToast({
                status: 'danger',
                message: getErrorMessage(error)
            });
        });
        fetchCalendarEventTypes()
            .then(function (items) {
            setEventTypes(items);
        })
            .catch(function (error) {
            setToast({
                status: 'danger',
                message: getErrorMessage(error)
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsx(AppContext.Provider, __assign({ value: { isGoogleMapAPILoaded: isLoaded, googleMapAPILoadError: loadError } }, { children: _jsxs("div", __assign({ className: "flex grow h-0 bg-white overflow-hidden p-3" }, { children: [_jsxs("div", __assign({ className: "w-[330px] p-3 box-content flex-none flex flex-col" }, { children: [_jsx(Card, __assign({ className: "bg-gray-50 shadow-[0px_3px_8px_0px_rgba(87,102,117,0.06),_0px_6px_12px_0px_rgba(106,115,129,0.16)] rounded-lg" }, { children: _jsxs("div", __assign({ className: "pl-4 pr-2 py-4 text-gray-600 flex items-center justify-between" }, { children: [_jsxs("div", __assign({ className: "flex flex-col gap-2" }, { children: [_jsxs("div", __assign({ className: "text-base leading-none text-text-default" }, { children: ["Office: ", "".concat(selectedOffice ? "".concat(selectedOffice.region, " /") : 'N/A')] })), selectedOffice && (_jsx("div", __assign({ className: "text-xl leading-6 text-text-default" }, { children: selectedOffice.name })))] })), _jsxs("div", __assign({ className: "flex items-center justify-center h-[40px] cursor-pointer", onClick: openOfficeSelectionModal }, { children: [_jsx(AptiveIcon, { className: "w-[24px] h-[24px] stroke-gray-600 fill-none", icon: "globeAlt" }), _jsxs("div", __assign({ className: "w-[16px] h-[24px] flex flex-col items-center relative" }, { children: [_jsx(AptiveIcon, { className: "w-[16px] h-[12px] stroke-gray-600 fill-none absolute bottom-2.5", icon: "chevronUp" }), _jsx(AptiveIcon, { className: "w-[16px] h-[12px] stroke-gray-600 fill-none absolute top-2.5", icon: "chevronDown" })] }))] }))] })) })), _jsx(Card, __assign({ className: "py-4 px-[31px]" }, { children: _jsx(MiniCalendar, { className: "px-2 py-4", currentDate: currentDate, view: "month", onChange: handleDateChange }) })), _jsx(CalendarDayList, { data: currentWeekDates })] })), selectedOffice && (_jsxs(_Fragment, { children: [_jsx(OfficeCalendar, { officeId: selectedOffice === null || selectedOffice === void 0 ? void 0 : selectedOffice.id, onClickAddNewEvent: openNewEventModal, onSuccess: handleEventSuccess, eventTypes: eventTypes, activeDate: currentDate, viewMode: calendarMode, setViewMode: setCalendarMode }, officeCalendarKey), _jsx(OfficeModal, { open: isOfficeModalOpen, className: "w-[658px] min-h-[599px]", selectedOffice: selectedOffice, regions: regions, onSelectOffice: handleChangeOffice, onClose: closeOfficeSelectionModal, center: selectedOffice.location, appContext: AppContext })] })), isNewEventModalOpen && (_jsx(EventUpdateModal, { isOpen: isNewEventModalOpen, setIsOpen: setIsNewEventModalOpen, eventParticipants: [], updateType: EVENT_NEW_TYPE, onClose: closeNewEventModal, onSuccess: handleEventSuccess })), noOfficeModalOpen && (_jsx(NoOffice, { isOpen: noOfficeModalOpen, onClose: function () { return setNoOfficeModalOpen(false); } }))] })) })));
};
export default Calendar;
